import React, { useState, useContext } from "react";
import {
  View,
  Text,
  TextInput,
  ActivityIndicator,
  StyleSheet,
  Alert,
  TouchableOpacity,
  Platform,
  KeyboardAvoidingView,
  useWindowDimensions,
  ScrollView,
  Image,
} from "react-native";
import { db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { AuthContext } from "../Context/AuthContext";
import GlobalStyles from "../components/StylenColor";
import sendEmployeeEmail from "../components/EmpMail";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";
import { NetworkContext } from "../Context/NetworkContext";
import { LinearGradient } from "expo-linear-gradient";

// Desktop web imports
import { useDesktopDetect, DesktopTheme } from "../components/web";

const { colors, typography, spacing, borderRadius, shadows } = DesktopTheme;

// Desktop Employee Login View
const DesktopEmployeeLoginView = ({
  companyCode,
  setCompanyCode,
  employeeId,
  setEmployeeId,
  password,
  setPassword,
  isLoading,
  handleLogin,
  forgotPassword,
  navigation,
}) => {
  const [codeFocused, setCodeFocused] = useState(false);
  const [idFocused, setIdFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  return (
    <View style={desktopStyles.container}>
      <View style={desktopStyles.splitContainer}>
        {/* Left Panel - Branding */}
        <View style={desktopStyles.brandPanel}>
          <View style={desktopStyles.brandContent}>
            <Image
              source={require("../assets/ESG_logo.png")}
              style={desktopStyles.logo}
              resizeMode="contain"
            />
            <Text style={desktopStyles.brandTitle}>ESG Tracker</Text>
            <Text style={desktopStyles.brandTagline}>
              Employee Portal
            </Text>
            <View style={desktopStyles.featureList}>
              <View style={desktopStyles.featureItem}>
                <Text style={desktopStyles.featureIcon}>📝</Text>
                <Text style={desktopStyles.featureText}>Complete Assessments</Text>
              </View>
              <View style={desktopStyles.featureItem}>
                <Text style={desktopStyles.featureIcon}>🎯</Text>
                <Text style={desktopStyles.featureText}>Track Your Progress</Text>
              </View>
              <View style={desktopStyles.featureItem}>
                <Text style={desktopStyles.featureIcon}>🏆</Text>
                <Text style={desktopStyles.featureText}>Contribute to ESG Goals</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Right Panel - Login Form */}
        <View style={desktopStyles.formPanel}>
          <View style={desktopStyles.formContainer}>
            <Text style={desktopStyles.formTitle}>Employee Login</Text>
            <Text style={desktopStyles.formSubtitle}>
              Sign in with your company credentials
            </Text>

            <View style={desktopStyles.inputGroup}>
              <Text style={desktopStyles.inputLabel}>Company Code</Text>
              <TextInput
                style={[
                  desktopStyles.input,
                  codeFocused && desktopStyles.inputFocused,
                ]}
                placeholder="Enter company code"
                value={companyCode}
                onChangeText={setCompanyCode}
                autoCapitalize="characters"
                placeholderTextColor={colors.textMuted}
                onFocus={() => setCodeFocused(true)}
                onBlur={() => setCodeFocused(false)}
              />
            </View>

            <View style={desktopStyles.inputGroup}>
              <Text style={desktopStyles.inputLabel}>Employee ID</Text>
              <TextInput
                style={[
                  desktopStyles.input,
                  idFocused && desktopStyles.inputFocused,
                ]}
                placeholder="Enter your employee ID"
                value={employeeId}
                onChangeText={setEmployeeId}
                placeholderTextColor={colors.textMuted}
                onFocus={() => setIdFocused(true)}
                onBlur={() => setIdFocused(false)}
              />
            </View>

            <View style={desktopStyles.inputGroup}>
              <Text style={desktopStyles.inputLabel}>Password</Text>
              <TextInput
                style={[
                  desktopStyles.input,
                  passwordFocused && desktopStyles.inputFocused,
                ]}
                placeholder="Enter your password"
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                placeholderTextColor={colors.textMuted}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
              />
            </View>

            {isLoading ? (
              <View style={desktopStyles.loadingButton}>
                <ActivityIndicator size="small" color="#fff" />
              </View>
            ) : (
              <TouchableOpacity
                style={desktopStyles.loginButton}
                onPress={handleLogin}
              >
                <Text style={desktopStyles.loginButtonText}>Sign In</Text>
              </TouchableOpacity>
            )}

            <View style={desktopStyles.divider}>
              <View style={desktopStyles.dividerLine} />
              <Text style={desktopStyles.dividerText}>or</Text>
              <View style={desktopStyles.dividerLine} />
            </View>

            <View style={desktopStyles.linkGroup}>
              <TouchableOpacity
                onPress={() => navigation.navigate("EmployeeRegistrationScreen")}
                style={desktopStyles.linkButton}
              >
                <Text style={desktopStyles.linkText}>
                  New employee?{" "}
                  <Text style={desktopStyles.linkTextBold}>Register Here</Text>
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={forgotPassword}
                style={desktopStyles.linkButton}
              >
                <Text style={desktopStyles.linkText}>
                  <Text style={desktopStyles.linkTextBold}>Forgot Password?</Text>
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => navigation.navigate("CompanyLoginScreen")}
                style={[desktopStyles.linkButton, { marginTop: spacing.md }]}
              >
                <Text style={desktopStyles.linkText}>
                  Company Admin?{" "}
                  <Text style={desktopStyles.linkTextBold}>Login Here</Text>
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const EmployeeLoginScreen = ({ navigation }) => {
  const [companyCode, setCompanyCode] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { setUser, setCompanyId, setCompanyName } = useContext(AuthContext);
  const { isConnected } = useContext(NetworkContext);
  const { width } = useWindowDimensions();
  const isWide = width > 800;

  // Desktop detection
  const { isDesktopWeb } = useDesktopDetect();

  const showAlert = (title, message) => {
    if (Platform.OS === "web") {
      window.alert(`${title}\n\n${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  const validateInputs = () => {
    if (!companyCode || !employeeId || !password) {
      showAlert("Error", "All fields are required!");
      return false;
    }
    return true;
  };

  const handleLogin = async () => {
    if (!validateInputs()) return;
    if (!isConnected) {
      showAlert("No Internet", "You're currently offline.");
      return;
    }
    setIsLoading(true);

    try {
      const companyQuery = query(
        collection(db, "companies"),
        where("companyCode", "==", companyCode)
      );
      const companySnapshot = await getDocs(companyQuery);

      if (companySnapshot.empty) {
        showAlert("Error", "No company found with this code!");
        setIsLoading(false);
        return;
      }

      const companyDoc = companySnapshot.docs[0];
      const companyId = companyDoc.id;
      const companyName = companyDoc.data().name;

      const employeeQuery = query(
        collection(db, `companies/${companyId}/employees`),
        where("employeeId", "==", employeeId),
        where("password", "==", password)
      );
      const employeeSnapshot = await getDocs(employeeQuery);

      if (!employeeSnapshot.empty) {
        const employeeData = employeeSnapshot.docs[0].data();

        setUser({
          employeeIdNum: employeeData.employeeId,
          employeeId: employeeSnapshot.docs[0].id,
          department: employeeData.department,
          name: employeeData.name,
          companyId: companyId,
          companyName: companyName,
          topic: employeeData.topic,
        });
        setCompanyId(companyId);
        setCompanyName(companyName);
        navigation.navigate("QuestionnaireTopicsScreen");
      } else {
        showAlert("Error", "Invalid Employee ID or Password!");
      }
    } catch (error) {
      console.error("Login error:", error);
      showAlert("Error", "Login failed. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async () => {
    if (!companyCode || !employeeId) {
      showAlert(
        "Input Required",
        "Please enter your Company Code and Employee ID."
      );
      return;
    }

    try {
      const companySnapshot = await getDocs(
        query(
          collection(db, "companies"),
          where("companyCode", "==", companyCode)
        )
      );

      if (companySnapshot.empty) {
        showAlert("Error", "No company found with this code!");
        return;
      }

      const companyDoc = companySnapshot.docs[0];
      const companyId = companyDoc.id;
      const companyName = companyDoc.data().name;

      const employeeSnapshot = await getDocs(
        query(
          collection(db, `companies/${companyId}/employees`),
          where("employeeId", "==", employeeId)
        )
      );

      if (employeeSnapshot.empty) {
        showAlert("Error", "No employee found with this ID!");
        return;
      }

      const employeeData = employeeSnapshot.docs[0].data();

      await sendEmployeeEmail(
        employeeData.email,
        employeeData.name || "Employee",
        employeeData.employeeId || "",
        employeeData.department || "",
        employeeData.password,
        companyName || "",
        "",
        true
      );

      showAlert(
        "Password Sent",
        `Your password has been sent to the email: ${employeeData.email}`
      );
    } catch (error) {
      console.error("Error sending password:", error);
      showAlert("Error", "Failed to send password. Please try again.");
    }
  };

  // Render desktop version
  if (isDesktopWeb) {
    return (
      <DesktopEmployeeLoginView
        companyCode={companyCode}
        setCompanyCode={setCompanyCode}
        employeeId={employeeId}
        setEmployeeId={setEmployeeId}
        password={password}
        setPassword={setPassword}
        isLoading={isLoading}
        handleLogin={handleLogin}
        forgotPassword={forgotPassword}
        navigation={navigation}
      />
    );
  }

  // Mobile version
  return (
    <LinearGradient
      colors={GlobalStyles.GlobalBg.gradientColors}
      style={styles.safeArea}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
    >
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 20}
      >
        <ScrollView contentContainerStyle={styles.scrollContainer}>
          <View
            style={[styles.mainContainer, isWide && styles.mainContainerWide]}
          >
            <View style={[styles.leftContainer, isWide && styles.leftWide]}>
              <Image
                source={require("../assets/ESG_logo.png")}
                style={styles.logo}
                resizeMode="contain"
              />
              <Text
                style={[
                  styles.tagline,
                  { marginBottom: 4 },
                  isWide && { fontSize: GlobalStyles.fontSize.sm },
                ]}
              >
                Lighting the Way
              </Text>
              <Text
                style={[
                  styles.tagline,
                  isWide && { fontSize: GlobalStyles.fontSize.sm },
                ]}
              >
                Towards Sustainability
              </Text>
            </View>

            <View style={[styles.rightContainer, isWide && styles.rightWide]}>
              <View style={styles.formWrapper}>
                <Text
                  style={[
                    styles.title,
                    GlobalStyles.titleTxtOffset,
                    isWide && { fontSize: GlobalStyles.fontSize.lg + 1 },
                  ]}
                >
                  Employee Login
                </Text>

                <TextInput
                  style={styles.input}
                  placeholder="Company Code"
                  value={companyCode}
                  onChangeText={setCompanyCode}
                  autoCapitalize="none"
                  placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
                  selectionColor={GlobalStyles.inpColor.colors.selection}
                />

                <TextInput
                  style={styles.input}
                  placeholder="Employee ID"
                  value={employeeId}
                  onChangeText={setEmployeeId}
                  placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
                  selectionColor={GlobalStyles.inpColor.colors.selection}
                />

                <TextInput
                  style={styles.input}
                  placeholder="Password"
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry
                  placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
                  selectionColor={GlobalStyles.inpColor.colors.selection}
                />

                {isLoading ? (
                  <ActivityIndicator
                    size="large"
                    color={GlobalStyles.btnColor.backgroundColor}
                    style={styles.loginButton}
                  />
                ) : (
                  <TouchableOpacity
                    style={[styles.loginButton, GlobalStyles.btnBgShadow]}
                    onPress={handleLogin}
                  >
                    <Text
                      style={[
                        styles.loginButtonText,
                        GlobalStyles.btntxtoffset,
                        isWide && { fontSize: GlobalStyles.fontSize.sm },
                      ]}
                    >
                      Login
                    </Text>
                  </TouchableOpacity>
                )}

                <TouchableOpacity
                  onPress={() =>
                    navigation.navigate("EmployeeRegistrationScreen")
                  }
                  style={styles.registerLink}
                >
                  <Text style={styles.registerText}>
                    Not registered? Register as an employee
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={forgotPassword}
                  style={styles.forgotPasswordLink}
                >
                  <Text style={styles.registerText}>Forgot Password ?</Text>
                </TouchableOpacity>
              </View>

              <View style={styles.footer}>
                <Text style={styles.footerText}>
                  All rights are Reserved under Sowin Enterprises® 2025
                </Text>
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </LinearGradient>
  );
};

// Desktop styles
const desktopStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.contentBg,
  },
  splitContainer: {
    flex: 1,
    flexDirection: 'row',
    minHeight: '100vh',
  },

  // Brand Panel
  brandPanel: {
    flex: 1,
    backgroundColor: colors.sidebarBg,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxl,
  },
  brandContent: {
    alignItems: 'center',
    maxWidth: 400,
  },
  logo: {
    width: 120,
    height: 120,
    marginBottom: spacing.lg,
  },
  brandTitle: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h1,
    color: '#fff',
    marginBottom: spacing.sm,
  },
  brandTagline: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.h4,
    color: colors.sidebarTextMuted,
    textAlign: 'center',
    marginBottom: spacing.xxl,
  },
  featureList: {
    width: '100%',
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
    paddingHorizontal: spacing.lg,
  },
  featureIcon: {
    fontSize: 24,
    marginRight: spacing.md,
  },
  featureText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.sidebarText,
  },

  // Form Panel
  formPanel: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxl,
    backgroundColor: colors.cardBg,
  },
  formContainer: {
    width: '100%',
    maxWidth: 400,
  },
  formTitle: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h2,
    color: colors.textPrimary,
    marginBottom: spacing.xs,
  },
  formSubtitle: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textMuted,
    marginBottom: spacing.xl,
  },

  // Input
  inputGroup: {
    marginBottom: spacing.md,
  },
  inputLabel: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.small,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  input: {
    width: '100%',
    height: 48,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md,
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textPrimary,
    backgroundColor: colors.contentBg,
  },
  inputFocused: {
    borderColor: colors.primary,
    borderWidth: 2,
  },

  // Button
  loginButton: {
    width: '100%',
    height: 48,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.md,
    ...shadows.sm,
  },
  loadingButton: {
    width: '100%',
    height: 48,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.md,
    opacity: 0.7,
  },
  loginButtonText: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.body,
    color: '#fff',
  },

  // Divider
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: spacing.lg,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.border,
  },
  dividerText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textMuted,
    marginHorizontal: spacing.md,
  },

  // Links
  linkGroup: {
    alignItems: 'center',
  },
  linkButton: {
    paddingVertical: spacing.sm,
  },
  linkText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textSecondary,
  },
  linkTextBold: {
    fontFamily: typography.fontFamily.heading,
    color: colors.primary,
  },
});

// Mobile styles
const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: "center",
    padding: 24,
  },
  mainContainer: {
    flexDirection: "column",
    alignItems: "center",
  },
  mainContainerWide: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    maxWidth: 1000,
    alignSelf: "center",
    minHeight: "80vh",
  },
  formWrapper: {
    width: "100%",
    maxWidth: 350,
    alignItems: "center",
    backgroundColor: "#bdd9c2",
    padding: 20,
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
  },
  leftContainer: {
    alignItems: "center",
    marginBottom: 30,
  },
  leftWide: {
    width: "50%",
    alignItems: "flex-start",
    justifyContent: "center",
    paddingHorizontal: 20,
  },
  rightContainer: {
    width: "100%",
    alignItems: "center",
  },
  rightWide: {
    width: "50%",
    paddingTop: 20,
    alignItems: "flex-end",
    justifyContent: "center",
    paddingHorizontal: 20,
  },
  logo: {
    width: 160,
    height: 160,
    marginBottom: 10,
  },
  tagline: {
    paddingVertical: 2,
    fontSize: GlobalStyles.fontSize.md,
    color: GlobalStyles.btnColor.backgroundColor,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    textAlign: "center",
    lineHeight: 25,
  },
  title: {
    fontSize: GlobalStyles.fontSize.xxxl,
    marginBottom: 25,
    color: GlobalStyles.btnColor.backgroundColor,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    textAlign: "center",
  },
  input: {
    color: "#000",
    width: "100%",
    maxWidth: 350,
    fontSize: 16,
    height: 50,
    borderColor: "#e0e0e0",
    borderWidth: 1,
    borderRadius: 10,
    marginBottom: 18,
    paddingHorizontal: 15,
    backgroundColor: "#fff",
    fontFamily: GlobalStyles.inpFont.fontFamily,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  loginButton: {
    width: "100%",
    maxWidth: 350,
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: "center",
    marginBottom: 16,
  },
  loginButtonText: {
    color: "#fff",
    fontSize: GlobalStyles.fontSize.md,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  registerLink: {
    marginTop: 15,
  },
  registerText: {
    color: GlobalStyles.btnColor.backgroundColor,
    fontSize: GlobalStyles.fontSize.xss,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    textDecorationLine: "underline",
  },
  forgotPasswordLink: {
    marginTop: 15,
  },
  footer: {
    alignItems: "center",
    marginTop: 50,
  },
  footerText: {
    fontSize: GlobalStyles.fontSize.xsss,
    color: "#777",
    fontFamily: GlobalStyles.inpFont.fontFamily,
  },
});

export default EmployeeLoginScreen;
